import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from "@material-ui/core";
import { useTranslation } from "localization/translation";

const buildInitialData = (parentPlan, currencies) => {
  const settings = parentPlan?.settings || {};
  const supportedCurrencies = (currencies || []).filter((currency) =>
    parentPlan?.prices?.some((price) => price.currency === currency.code)
  );
  const prices = supportedCurrencies.flatMap((currency) =>
    ["monthly", "annual"].map((billingPeriod) => {
      const parentPrice = parentPlan?.prices?.find(
        (price) =>
          price.currency === currency.code &&
          price.billing_period === billingPeriod
      );
      return {
        currency_id: currency.id,
        currency: currency.code,
        billing_period: billingPeriod,
        price: parentPrice?.price || "",
        active: true,
      };
    })
  );

  return {
    name_en: "Lite",
    name_hy: "Lite",
    name_ru: "Lite",
    settings: {
      trial_period_days: settings.trial_period_days || 0,
      max_inventory_items: settings.max_inventory_items ?? null,
      max_inventory_items_ml: settings.max_inventory_items_ml ?? 0,
      max_inventory_items_kg_pc: settings.max_inventory_items_kg_pc ?? 0,
      unlimited_items: false,
      max_additional_branches: settings.max_additional_branches || 0,
      unlimited_branches: false,
      additional_support: false,
    },
    prices,
    feature_ids: [],
  };
};

const CreateChildPlanModal = ({
  isOpen,
  close,
  accept,
  loading,
  parentPlan,
  currencies,
  error,
}) => {
  const translation = useTranslation();
  const [data, setData] = useState(() =>
    buildInitialData(parentPlan, currencies)
  );

  useEffect(() => {
    if (isOpen) {
      setData(buildInitialData(parentPlan, currencies));
    }
  }, [isOpen, parentPlan, currencies]);

  const changeSetting = (field, value) => {
    setData((previous) => ({
      ...previous,
      settings: {
        ...previous.settings,
        [field]: value,
      },
    }));
  };

  const changePrice = (currencyId, billingPeriod, value) => {
    setData((previous) => ({
      ...previous,
      prices: previous.prices.map((price) =>
        price.currency_id === currencyId &&
        price.billing_period === billingPeriod
          ? { ...price, price: value }
          : price
      ),
    }));
  };

  const submit = (event) => {
    event.preventDefault();
    accept({
      ...data,
      settings: {
        ...data.settings,
        trial_period_days: Number(data.settings.trial_period_days),
        max_inventory_items:
          data.settings.max_inventory_items === null
            ? null
            : Number(data.settings.max_inventory_items),
        max_inventory_items_ml: Number(data.settings.max_inventory_items_ml),
        max_inventory_items_kg_pc: Number(
          data.settings.max_inventory_items_kg_pc
        ),
        max_additional_branches: Number(
          data.settings.max_additional_branches
        ),
      },
      prices: data.prices.map(({ currency, ...price }) => ({
        ...price,
        currency_id: Number(price.currency_id),
        price: Number(price.price),
      })),
    });
  };

  return (
    <Dialog
      classes={{ paper: "modal-content rounded-lg" }}
      open={isOpen}
      onClose={close}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={submit}>
        <DialogTitle>{translation.create_light_plan}</DialogTitle>
        <DialogContent>
          <div className="row">
            {["en", "hy", "ru"].map((language) => (
              <div className="col-md-4 my-2" key={language}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  label={`${translation.name} (${language.toUpperCase()})`}
                  value={data[`name_${language}`]}
                  onChange={(event) =>
                    setData((previous) => ({
                      ...previous,
                      [`name_${language}`]: event.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-md-4 my-2">
              <TextField
                required
                fullWidth
                type="number"
                variant="outlined"
                label={translation.trial_period_days}
                value={data.settings.trial_period_days}
                onChange={(event) =>
                  changeSetting("trial_period_days", event.target.value)
                }
              />
            </div>
            <div className="col-md-4 my-2">
              <TextField
                required
                fullWidth
                type="number"
                variant="outlined"
                label={`${translation.max_inventories_count} (${translation.ml})`}
                value={data.settings.max_inventory_items_ml}
                onChange={(event) =>
                  changeSetting("max_inventory_items_ml", event.target.value)
                }
                disabled={data.settings.unlimited_items}
              />
            </div>
            <div className="col-md-4 my-2">
              <TextField
                required
                fullWidth
                type="number"
                variant="outlined"
                label={`${translation.max_inventories_count} (${translation.kg}/${translation.pc})`}
                value={data.settings.max_inventory_items_kg_pc}
                onChange={(event) =>
                  changeSetting("max_inventory_items_kg_pc", event.target.value)
                }
                disabled={data.settings.unlimited_items}
              />
            </div>
          </div>

          <FormControlLabel
            control={
              <Switch
                checked={data.settings.unlimited_items}
                onChange={(event) =>
                  changeSetting("unlimited_items", event.target.checked)
                }
              />
            }
            label={translation.unlimited_items || "Unlimited items"}
          />

          <h6 className="mt-3">{translation.prices || "Prices"}</h6>
          <div className="row">
            {data.prices.map((price) => (
              <div
                className="col-md-4 my-2"
                key={`${price.currency_id}-${price.billing_period}`}
              >
                <TextField
                  required
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  variant="outlined"
                  label={`${price.currency} ${price.billing_period}`}
                  value={price.price}
                  onChange={(event) =>
                    changePrice(
                      price.currency_id,
                      price.billing_period,
                      event.target.value
                    )
                  }
                />
              </div>
            ))}
          </div>
          {error && <div className="text-danger mt-2">{error}</div>}
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={close} variant="contained" type="button">
            {translation.no}
          </Button>
          <Button
            color="secondary"
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : translation.create}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateChildPlanModal;
